import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notify, subscribeToNotifications } from '../../services/notify.js';

const tones = {
  success: {
    icon: CheckCircle2,
    surface: 'border-state-ok/25 bg-white',
    accent: 'bg-state-ok/10 text-state-ok',
  },
  error: {
    icon: AlertCircle,
    surface: 'border-state-critical/25 bg-white',
    accent: 'bg-state-critical/10 text-state-critical',
  },
  info: {
    icon: Info,
    surface: 'border-beam-blue/20 bg-white',
    accent: 'bg-beam-blue/10 text-beam-blue',
  },
};

export const useToast = () => notify;

export const ToastViewport = () => {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);

  useEffect(
    () =>
      subscribeToNotifications((toast) => {
        setToasts((current) => [...current.slice(-3), toast]);
        if (toast.duration > 0) {
          window.setTimeout(() => {
            setToasts((current) => current.filter((item) => item.id !== toast.id));
          }, toast.duration);
        }
      }),
    [],
  );

  const dismiss = (id) => setToasts((current) => current.filter((item) => item.id !== id));

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:top-0 sm:items-end sm:p-6"
    >
      {toasts.map((toast) => {
        const tone = tones[toast.tone] || tones.info;
        const Icon = tone.icon;

        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border p-3 shadow-crisp ${tone.surface}`}
          >
            <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${tone.accent}`}>
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1 pt-1">
              {toast.title ? <p className="text-sm font-extrabold text-beam-ink">{toast.title}</p> : null}
              <p className={`text-sm font-semibold text-beam-graphite ${toast.title ? 'mt-1' : ''}`}>
                {toast.message}
              </p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="focus-ring grid h-8 w-8 shrink-0 place-items-center rounded-lg text-beam-graphite/60 hover:bg-beam-light hover:text-beam-ink"
              aria-label={t('common.dismiss')}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
